import useSWR, { mutate } from "swr";
import type {
	ICloseBoard,
	ICloseDetail,
	ICloseEvidence,
	ICloseLateness,
	ICloseLookup,
	ICloseRow,
	ICloseWorkload,
	IMyWeek,
} from "types/close.types";
import { type PaginatedResponse, ProjectStatus } from "types/project.types";
import type { ITask } from "types/task.types";
import { api } from "utils/axios";
import { invalidateProject } from "./invalidateProject";
import { updateFetcher } from "./useTask";

/**
 * The close tracker's reads.
 *
 * Conventions follow `useFirmCloseBoard` / `useCloseChecklist` in this folder:
 * the global SWR fetcher supplies the transport, the key IS the endpoint path,
 * and a key of `null` means "do not fetch" rather than "fetch with nothing".
 */

/** One row per client for a period (R2). */
export function useCloseBoard(period?: string, teamId?: number) {
	const params = new URLSearchParams();
	if (period) params.set("period", period);
	if (teamId != null) params.set("teamId", String(teamId));
	const qs = params.toString();
	const { data, error, isLoading, mutate } = useSWR<ICloseBoard>(
		`close/board${qs ? `?${qs}` : ""}`,
	);
	return { board: data, error, isLoading, revalidate: () => mutate() };
}

/**
 * Who is carrying the open close work (`GET close/workload`), for the Team
 * view. Same `period` and `teamId` as the board, so the two can never describe
 * different months or different teams. `enabled=false` skips the fetch until
 * the view is actually opened.
 */
export function useCloseWorkload(
	period?: string,
	teamId?: number,
	enabled = true,
) {
	const params = new URLSearchParams();
	if (period) params.set("period", period);
	if (teamId != null) params.set("teamId", String(teamId));
	const qs = params.toString();
	const { data, error, isLoading } = useSWR<ICloseWorkload>(
		enabled ? `close/workload${qs ? `?${qs}` : ""}` : null,
	);
	return { workload: data, error, isLoading };
}

/** The signed-in user's own queue, most urgent group first (R5). */
export function useMyWeek(enabled = true) {
	const { data, error, isLoading, mutate } = useSWR<IMyWeek>(
		enabled ? "close/my-week" : null,
	);
	return { myWeek: data, error, isLoading, revalidate: () => mutate() };
}

/**
 * One close with its full checklist (R3).
 *
 * `GET close/:closeId` returns the board row plus the ordered steps, so the
 * pane can show each step's title, owner, due date and state, and tick them.
 */
export function useCloseDetail(closeId?: number | null) {
	const { data, error, isLoading, mutate } = useSWR<ICloseDetail>(
		closeId ? `close/${closeId}` : null,
	);
	return {
		detail: data,
		error,
		isLoading,
		revalidate: () => mutate(),
	};
}

/**
 * Which close the company page shows for a client and period
 * (`GET close/by-company-period`). `period` is the address's token: a month
 * (`2026-08`), a quarter (`2026-Q3`) or a year (`2026`), which the API reads
 * as that exact period, so `2026-09` never finds the Q3 close that shares its
 * month text. A null `companyId` or `period` skips the fetch, so a malformed
 * URL never reaches the API.
 */
export function useCloseLookup(
	companyId: number | null,
	period: string | null,
) {
	const { data, error, isLoading, mutate } = useSWR<ICloseLookup>(
		companyId && period
			? `close/by-company-period?companyId=${companyId}&period=${period}`
			: null,
	);
	return { lookup: data, error, isLoading, revalidate: () => mutate() };
}

/**
 * Per-account reconciliation evidence for one close (`GET close/:closeId/evidence`).
 * Its own request, like lateness: a failure here shows "Couldn't load" in the
 * Accounts section and never blanks the checklist or disables sign-off.
 */
export function useCloseEvidence(closeId?: number | null) {
	const { data, error, isLoading, mutate } = useSWR<ICloseEvidence>(
		closeId ? `close/${closeId}/evidence` : null,
	);
	return { evidence: data, error, isLoading, revalidate: () => mutate() };
}

/**
 * Recurring lateness for a close's steps, judged over the client's earlier
 * closes. Its own request rather than a field on `close/:closeId`, so a slow
 * history read never delays the checklist, and a failed one just draws nothing.
 */
export function useCloseLateness(closeId?: number | null) {
	const { data } = useSWR<ICloseLateness>(
		closeId ? `close/${closeId}/lateness` : null,
	);
	return data;
}

/**
 * Far above any real checklist. The endpoint defaults to 20 rows, which would
 * silently undercount a long close; `complete` below catches it if one ever
 * outgrows this.
 */
const CLOSE_TASKS_LIMIT = 500;

/**
 * The key (and path) for a close's tasks. `parentOnly=false` because the list
 * holds parents only by default, and a default task's subtasks (EQ-2361) are
 * real tasks whose statuses and cancellations the close must see.
 */
export function closeTaskStatusesKey(closeId?: number | null): string | null {
	return closeId
		? `tasks?project=${closeId}&parentOnly=false&limit=${CLOSE_TASKS_LIMIT}&offset=0`
		: null;
}

/**
 * A close's tasks as Project Management holds them (`GET tasks?project=`), for
 * each task's own status. `close/:closeId` reports only a done flag, which folds
 * Completed and Canceled together and cannot say In Progress or On Hold.
 *
 * `complete` is false when the server holds more tasks than came back, so the
 * caller can refuse to show counts that would be wrong.
 */
export function useCloseTaskStatuses(closeId?: number | null) {
	const { data, error, isLoading, mutate } = useSWR<PaginatedResponse<ITask>>(
		closeTaskStatusesKey(closeId),
	);
	return {
		tasks: data?.data,
		complete: data ? data.count <= data.data.length : true,
		error,
		isLoading,
		revalidate: () => mutate(),
	};
}

/**
 * Tick or untick one step.
 *
 * 🪤 Unticking a step the product ticked is recorded server-side as a manual
 * override, and the automation then leaves that step alone. That is deliberate:
 * the human wins. Don't "fix" a step that stays unticked after a reconciliation
 * completes — somebody unticked it on purpose.
 */
export function useStepActions(
	onChanged: () => void,
	{ isCallerScoped = false }: { isCallerScoped?: boolean } = {},
) {
	// Every close write also refreshes Project Management's reads of the same
	// rows (the task lists, project detail, the Projects list), so a tick here
	// is on screen there without a reload. A close's own project page shows
	// none of the others and refreshes its own keys in `onChanged`
	// (`revalidateClose`), so it opts out with `isCallerScoped`.
	const changed = () => {
		onChanged();
		if (!isCallerScoped) void invalidateProject(mutate);
	};
	const setDone = async (stepId: number, done: boolean) => {
		// The same write Project Management makes. `updateFetcher` turns the
		// route's 200 `{success:false, message}` refusals (no access, an empty
		// payload, a reviewer sign-off) into a rejection carrying the reason.
		try {
			await updateFetcher("tasks", {
				arg: {
					id: stepId,
					status: done ? ProjectStatus.Completed : ProjectStatus.New,
				},
			});
		} catch (err) {
			const reason = (err as { response?: { data?: { message?: unknown } } })
				?.response?.data?.message;
			if (typeof reason === "string" && reason) throw new Error(reason);
			throw err;
		}
		changed();
	};

	const signOff = async (closeId: number) => {
		await api.post(`close/${closeId}/sign-off`);
		changed();
	};

	const sendBack = async (closeId: number, note: string) => {
		await api.post(`close/${closeId}/send-back`, { note });
		changed();
	};

	/**
	 * Name one person on a step, or clear it with null. Stored only: the server
	 * sends no notification and keeps close steps out of the task list, so this
	 * never turns a step into somebody's personal to-do.
	 */
	const assign = async (stepId: number, userId: number | null) => {
		await api.post(`close/steps/${stepId}/assignee`, { userId });
		changed();
	};

	/**
	 * Name exactly this set of people on a step; an empty set clears it
	 * (EQ-2491). Sends `userIds` only, never `userId`. A subtask's pop-up reads
	 * its people from its parent's subtask list, so that list is refreshed too.
	 */
	const assignMany = async (
		stepId: number,
		userIds: number[],
		parentId?: number | null,
	) => {
		await api.post(`close/steps/${stepId}/assignee`, { userIds });
		if (parentId) void mutate(`/tasks/${parentId}/subtasks`);
		onChanged();
		// Every task list shows the step's people, wherever the pop-up opened.
		void invalidateProject(mutate);
	};

	/**
	 * EQU-772: add an account to the close's Reconcile task as a subtask. The
	 * server resolves the parent, the title and the `source_ref` — the account
	 * id is all it is told, so the browser can never choose what evidence ticks
	 * a task.
	 *
	 * The route answers a refusal as HTTP 200 with `{success: false, message}`,
	 * like the subtask route it writes through, so that case is turned into a
	 * rejection carrying the reason rather than reading as saved.
	 */
	const addAccountToTask = async (closeId: number, accountId: string) => {
		const res = await api.post(
			`close/${closeId}/accounts/${accountId}/task`,
			{},
		);
		if (res?.data?.success === false) {
			throw new Error(
				typeof res.data.message === "string" && res.data.message
					? res.data.message
					: "That account couldn't be added to the task.",
			);
		}
		changed();
	};

	return { setDone, signOff, sendBack, assign, assignMany, addAccountToTask };
}

/**
 * The two waiting actions the board row already carries enough state for.
 *
 * Both are keyed on the STEP id, which `row.waiting.stepId` supplies, so they
 * work without the missing `GET close/:closeId`. Ticking an arbitrary step and
 * the reminder-email composer do need it, and are not here.
 *
 * Neither swallows a failure: the caller shows the error, because a wait that
 * silently did not clear is worse than one that visibly failed to.
 */
export function useWaitingActions(onChanged: () => void) {
	/**
	 * Record that a step is waiting on the client.
	 *
	 * This is the half of the waiting flow that had no caller: the route, the
	 * service and the DTO all shipped, but nothing in the web app posted to it,
	 * so `waiting_on_client` could never occur and the tile, the stage mark, My
	 * Week's follow-up group and half the sidebar badge were structurally zero.
	 * `markReceived` and `snoozeFollowUp` below could only ever act on a wait
	 * nobody was able to set.
	 *
	 * `reason` is what the CLIENT will read, so the caller passes the step title,
	 * which the plan generator already names by period and account ("August bank
	 * statement, Chase checking ••1234"). An itemized, period-named ask is the
	 * only kind practitioners report getting answered.
	 */
	const askClient = async (
		stepId: number,
		reason: string,
		followUpOn: string,
	) => {
		await api.post(`close/steps/${stepId}/waiting`, { reason, followUpOn });
		onChanged();
	};

	const markReceived = async (stepId: number) => {
		await api.post(`close/steps/${stepId}/received`);
		onChanged();
	};

	const snoozeFollowUp = async (stepId: number, days: number) => {
		// `reminderSent` is deliberately NOT sent: pushing the date out is not
		// evidence that a reminder went out, and that count is the record used in
		// the conversation about a client who never responds.
		await api.post(`close/steps/${stepId}/snooze`, { days });
		onChanged();
	};

	return { askClient, markReceived, snoozeFollowUp };
}

/** Convenience: the row for one client on the board, if the board has it. */
export function findRow(
	board: ICloseBoard | undefined,
	companyId: number,
): ICloseRow | null {
	return board?.clients.find((c) => c.companyId === companyId)?.row ?? null;
}
